import { ConfidenceBadge } from '../stories/ConfidenceBadge'
import type { ConfidenceShadowDetail, ConfidenceShadowSummary } from '../../hooks/admin-stories'

/**
 * KAN-835 — the admin-only view of the SHADOW evidence-origin confidence.
 * Presentational only: the pages own the queries. Nothing here changes what
 * readers see; the copy says so, because an operator reading a diff must not
 * mistake it for the live label.
 */

const SHADOW_NOTE = 'Shadow only. Readers still see the current label.'

function humanise(code: string): string {
  return code.replace(/_/g, ' ')
}

export interface ConfidenceShadowPanelProps {
  /** undefined while loading; null when no shadow has been computed for the story. */
  shadow: ConfidenceShadowDetail | null | undefined
  isError?: boolean
  /** articleId → title, from the members the page already loaded. */
  titles?: Record<string, string>
}

export function ConfidenceShadowPanel({ shadow, isError = false, titles = {} }: ConfidenceShadowPanelProps) {
  return (
    <section aria-labelledby="confidence-shadow-heading">
      <h2
        id="confidence-shadow-heading"
        className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground"
      >
        Confidence (shadow)
      </h2>
      <p className="mb-2 text-xs text-muted-foreground">{SHADOW_NOTE}</p>
      {isError ? (
        <p className="text-sm text-destructive">Could not load the shadow confidence.</p>
      ) : shadow === undefined ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : shadow === null ? (
        <p className="text-sm text-muted-foreground">
          No shadow computed for this story. The shadow flag is off, or the story has not been recomputed since it was turned on.
        </p>
      ) : (
        <ShadowBody shadow={shadow} titles={titles} />
      )}
    </section>
  )
}

function ShadowBody({ shadow, titles }: { shadow: ConfidenceShadowDetail; titles: Record<string, string> }) {
  const labelChanged = shadow.currentLabel !== shadow.shadowLabel
  const title = (id: string) => titles[id] ?? id
  return (
    <div className="space-y-3 rounded border p-3">
      <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-xs text-muted-foreground">Current label</dt>
          <dd><ConfidenceBadge label={shadow.currentLabel} /></dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Shadow label</dt>
          <dd className="flex items-center gap-2">
            <ConfidenceBadge label={shadow.shadowLabel} />
            <span className="text-xs text-muted-foreground">{labelChanged ? 'differs' : 'same'}</span>
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Current canonical</dt>
          <dd className="truncate">{title(shadow.currentCanonicalArticleId)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Shadow canonical</dt>
          <dd className="truncate">
            {title(shadow.shadowCanonicalArticleId)}
            <span className="ml-2 text-xs text-muted-foreground">{shadow.canonicalChanged ? 'differs' : 'same'}</span>
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Independent origins</dt>
          <dd className="tabular-nums">
            {shadow.eligibleOriginCount} of {shadow.orgCount} organisations today
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Members not counted</dt>
          <dd className="tabular-nums">
            {shadow.ineligibleMemberCount} of {shadow.memberCount}
          </dd>
        </div>
      </dl>
      {shadow.reasonCodes.length > 0 && (
        <p className="text-xs text-muted-foreground">Why: {shadow.reasonCodes.map(humanise).join(', ')}</p>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <caption className="sr-only">Member-level evidence decisions</caption>
          <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Member</th>
              <th className="px-3 py-2 text-left">Role</th>
              <th className="px-3 py-2 text-left">Origin</th>
              <th className="px-3 py-2 text-left">Counted</th>
              <th className="px-3 py-2 text-left">Reasons</th>
            </tr>
          </thead>
          <tbody>
            {shadow.members.map((m) => (
              <tr key={m.articleId} className="border-t">
                <td className="max-w-xs truncate px-3 py-2">{title(m.articleId)}</td>
                <td className="px-3 py-2">{humanise(m.role)}</td>
                <td className="px-3 py-2">
                  {m.origin ?? '—'}
                  {m.originTier && <span className="ml-1 text-xs text-muted-foreground">({m.originTier})</span>}
                </td>
                <td className="px-3 py-2">{m.independenceEligible ? 'yes' : 'no'}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{m.reasonCodes.map(humanise).join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Policy {shadow.policyVersion} (generation {shadow.policyGeneration}) · computed {new Date(shadow.computedAt).toISOString().slice(0, 16).replace('T', ' ')} UTC
      </p>
    </div>
  )
}

export interface ConfidenceShadowSummaryCardProps {
  summary: ConfidenceShadowSummary | undefined
  isError?: boolean
}

export function ConfidenceShadowSummaryCard({ summary, isError = false }: ConfidenceShadowSummaryCardProps) {
  return (
    <section aria-labelledby="confidence-shadow-summary-heading" className="rounded border p-3">
      <h2 id="confidence-shadow-summary-heading" className="text-sm font-semibold">
        Confidence (shadow)
      </h2>
      <p className="mb-2 text-xs text-muted-foreground">{SHADOW_NOTE}</p>
      {isError ? (
        <p className="text-sm text-destructive">Could not load the shadow summary.</p>
      ) : !summary ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : summary.stories === 0 ? (
        <p className="text-sm text-muted-foreground">No shadow computed yet. The shadow flag is off, or no story has been recomputed since it was turned on.</p>
      ) : (
        <>
          <p className="text-sm tabular-nums">
            {summary.differing} of {summary.stories} stories differ: {summary.labelChanged} label, {summary.canonicalChanged} canonical.
          </p>
          <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
            {summary.transitions
              .filter((t) => t.from !== t.to)
              .map((t) => (
                <li key={`${t.from}-${t.to}`} className="tabular-nums">
                  {humanise(t.from)} → {humanise(t.to)}: {t.count}
                </li>
              ))}
          </ul>
          <p className="mt-2 text-xs text-muted-foreground">Policy {summary.policyVersions.join(', ') || '—'}</p>
        </>
      )}
    </section>
  )
}
